import React, { useEffect, useState } from 'react';
import taskService, { WorkflowTask } from '../../services/taskService';
import { commonService } from '../../services/commonService';
import UserPicker from './UserPicker';
import TaskChat from './TaskChat';
import StageStepper, { stageChipClass, stageLabel } from './StageStepper';
import AssigneeTree from './AssigneeTree';
import { hasPermission, getMyUserId } from '../../utils/permissions';
import '../../assets/styles/Workflow.css';

interface TaskWorkflowPanelProps {
  projectId: number;
  projectIdString: string; // the string projectid (e.g. project_id_...) needed for upload endpoints
  onStageChange?: (stage: string) => void;
}

export const STATUS_LABEL: Record<string, string> = {
  todo: 'To do',
  in_progress: 'In progress',
  qa_complete: 'QA Complete',
  done: 'Done',
  failed: 'Failed',
};

export const NEXT_STATUS: Record<string, Record<string, string>> = {
  build: { todo: 'in_progress', in_progress: 'done' },
  qa: { todo: 'in_progress', in_progress: 'qa_complete' },
  deliver: { todo: 'in_progress', in_progress: 'done' },
  other: { todo: 'in_progress', in_progress: 'done' },
};

const TaskWorkflowPanel: React.FC<TaskWorkflowPanelProps> = ({ projectId, projectIdString, onStageChange }) => {
  const [stage, setStage] = useState<string>('pending_build');
  const [tasks, setTasks] = useState<WorkflowTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subtaskForm, setSubtaskForm] = useState<Record<number, { kind: 'build' | 'qa' | 'deliver'; assigneeId: number | null; title: string }>>({});
  const [uploadingTaskId, setUploadingTaskId] = useState<number | null>(null);
  const myId = getMyUserId();

  const load = async () => {
    setLoading(true);
    try {
      const res = await taskService.getTasksByProject(projectId);
      setStage(res.stage);
      setTasks(res.tasks);
      onStageChange?.(res.stage);
    } catch (e) {
      setError('Failed to load workflow data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const rootTask = tasks.find((t) => t.parent_task_id === null);
  const subtasksOf = (taskId: number) => tasks.filter((t) => t.parent_task_id === taskId);

  const isAmAssignee = (task: WorkflowTask) => task.assignee_id === myId;
  const isRootAssignee = rootTask ? rootTask.assignee_id === myId : false;

  const updateStatus = async (task: WorkflowTask) => {
    const next = NEXT_STATUS[task.kind]?.[task.status];
    if (!next) return;
    setError(null);
    try {
      await taskService.updateTaskStatus(task.id, next);
      await load();
    } catch (e: any) {
      setError((e?.response?.data?.message || e?.response?.message) || 'Failed to update status');
    }
  };

  const submitSubtask = async (parentId: number) => {
    const draft = subtaskForm[parentId];
    if (!draft?.assigneeId) {
      setError('Pick an assignee for the subtask');
      return;
    }
    setError(null);
    try {
      await taskService.createSubtask({
        project_id: projectId,
        parent_task_id: parentId,
        kind: draft.kind,
        assignee_id: draft.assigneeId,
        title: draft.title || undefined,
      });
      setSubtaskForm((prev) => ({ ...prev, [parentId]: { kind: 'build', assigneeId: null, title: '' } }));
      await load();
    } catch (e: any) {
      setError((e?.response?.data?.message || e?.response?.message) || 'Failed to create subtask');
    }
  };

  const markReady = async () => {
    setError(null);
    try {
      await taskService.setProjectStage(projectId, 'ready_to_deliver');
      await load();
    } catch (e: any) {
      setError((e?.response?.data?.message || e?.response?.message) || 'Failed to mark ready to deliver');
    }
  };

  const uploadFileForTask = async (task: WorkflowTask, file: File) => {
    setUploadingTaskId(task.id);
    setError(null);
    try {
      const CHUNK_SIZE = 1 * 1024 * 1024;
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      const fileId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const formData = new FormData();
        formData.append('projectid', projectIdString);
        formData.append('file_id', fileId);
        formData.append('file_name', file.name);
        formData.append('chunk_index', String(i));
        formData.append('total_chunks', String(totalChunks));
        formData.append('file', chunk);
        await commonService.uploadChunk(formData);
      }
      await commonService.finalUpload(projectIdString, task.id);
      await updateStatus(task);
    } catch (e) {
      setError('File upload failed');
    } finally {
      setUploadingTaskId(null);
    }
  };

  const renderTaskCard = (task: WorkflowTask, isSubtask: boolean) => {
    const nextStatus = NEXT_STATUS[task.kind]?.[task.status];
    const canAdvance = isAmAssignee(task) && !!nextStatus;
    const canDelegate = hasPermission('fdc_task:create_subtask') && isAmAssignee(task) && task.status !== 'done' && task.status !== 'qa_complete';
    const draft = subtaskForm[task.id] || { kind: 'build' as const, assigneeId: null, title: '' };

    return (
      <div key={task.id} className="wf-task-card">
        <div className="wf-task-card-head">
          <div>
            <div className="wf-task-title">
              {task.kind === 'qa' ? '🔍 QA — ' : ''}{task.title || (isSubtask ? 'Subtask' : 'Build contact list')}
            </div>
            <div className="wf-task-meta">
              Assignee: {task.assignee_name || `User #${task.assignee_id}`} · Created by {task.created_by_name || `User #${task.created_by}`}
            </div>
          </div>
          <span className={`wf-status-chip wf-status-${task.status}`}>{STATUS_LABEL[task.status]}</span>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {canAdvance && (
            <button className="wf-btn wf-btn-primary" onClick={() => updateStatus(task)}>
              Mark {STATUS_LABEL[nextStatus!]}
            </button>
          )}
          {isAmAssignee(task) && task.kind === 'build' && (
            <label className="wf-btn" style={{ cursor: 'pointer' }}>
              {uploadingTaskId === task.id ? 'Uploading...' : '📎 Upload file'}
              <input
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                disabled={uploadingTaskId === task.id}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) uploadFileForTask(task, file);
                  e.target.value = '';
                }}
              />
            </label>
          )}
        </div>

        <TaskChat projectId={task.project_id} />

        {subtasksOf(task.id).length > 0 && (
          <div className="wf-subtask-list">
            {subtasksOf(task.id).map((st) => renderTaskCard(st, true))}
          </div>
        )}

        {canDelegate && (
          <div className="wf-subtask-list" style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>+ Delegate a subtask</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <select
                value={draft.kind}
                onChange={(e) => setSubtaskForm((prev) => ({ ...prev, [task.id]: { ...draft, kind: e.target.value as 'build' | 'qa' | 'deliver' } }))}
                style={{ padding: 6, borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12.5 }}
              >
                <option value="build">Build (PC)</option>
                <option value="qa">QA review</option>
              </select>
              <div style={{ minWidth: 220 }}>
                <UserPicker
                  role={draft.kind === 'qa' ? 'qa' : 'pc'}
                  value={draft.assigneeId}
                  onChange={(id) => setSubtaskForm((prev) => ({ ...prev, [task.id]: { ...draft, assigneeId: id } }))}
                  placeholder={draft.kind === 'qa' ? 'Assign to QA...' : 'Assign to PC...'}
                />
              </div>
              <button className="wf-btn wf-btn-primary" onClick={() => submitSubtask(task.id)}>Create</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) return <div style={{ fontSize: 13, color: '#6b7280' }}>Loading workflow...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <span className={stageChipClass(stage)}>{stageLabel(stage)}</span>
        {hasPermission('fdc_project:mark_ready') && isRootAssignee && (stage === 'in_progress' || stage === 'qa_review') && (
          <button className="wf-btn wf-btn-primary" onClick={markReady}>✓ Mark Ready to Deliver</button>
        )}
      </div>
      <StageStepper stage={stage} />
      {error && <div style={{ color: '#ef4444', fontSize: 12.5, marginBottom: 8 }}>{error}</div>}

      <div className="wf-field-label" style={{ marginTop: 14, marginBottom: 8 }}>Assignee tree</div>
      <AssigneeTree tasks={tasks} />

      <div className="wf-field-label" style={{ marginTop: 18, marginBottom: 8 }}>Tasks</div>
      {rootTask ? renderTaskCard(rootTask, false) : <div style={{ fontSize: 13, color: '#9ca3af' }}>No root task found.</div>}
    </div>
  );
};

export default TaskWorkflowPanel;
